import axios from 'axios'
import {
  WorldConfig,
  ChunkDynamicData,
  CastleData,
  AllianceAreaData,
  HexCoord,
  WorldAttackData,
  BarbarianVillageData,
} from './terrial-engine/types'

const BASE = '/world-map'

export type BarbarianVillageDefender = {
  _id: string
  characterId?: string
  characterName?: string
  characterImg?: string
  characterFrontView?: string
  rarity?: string
  level?: number
  isUsing?: boolean
  stats?: { maxHp?: number; currentHp?: number; attackPower?: number; armor?: number }
}

/** A timed garrison march (station = inbound, withdraw = returning home). */
export type BarbarianVillageTransfer = {
  id: string
  type: 'station' | 'withdraw'
  /** ISO timestamp when the champions arrive (defenders join / freed). */
  arrivalAt: string | null
  startedAt: string | null
  characters: BarbarianVillageDefender[]
}

/** Chunk fetches run in parallel (was one every 150ms — very slow for large viewports). */
const CHUNK_FETCH_CONCURRENCY = 8
let chunkFetchActive = 0
const chunkFetchWaiters: Array<() => void> = []

function acquireChunkFetchSlot(): Promise<void> {
  if (chunkFetchActive < CHUNK_FETCH_CONCURRENCY) {
    chunkFetchActive++
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    chunkFetchWaiters.push(() => {
      chunkFetchActive++
      resolve()
    })
  })
}

function releaseChunkFetchSlot() {
  chunkFetchActive = Math.max(0, chunkFetchActive - 1)
  const next = chunkFetchWaiters.shift()
  if (next) next()
}

// Cache for chunk requests to avoid duplicate requests
const chunkRequestCache = new Map<string, Promise<ChunkDynamicData>>()

/** Recent chunk payload — instant revisit; cleared on map mutations for this world. */
const chunkDataMemoryCache = new Map<string, ChunkDynamicData>()
const chunkDataMemoryTs = new Map<string, number>()
const CHUNK_DATA_MEMORY_TTL_MS = 20 * 60 * 1000

/** SPA session caches — avoids re-downloading huge lists when re-entering the world map. */
const castlesListCache = new Map<string, { list: CastleData[]; t: number }>()
const barbarianListCache = new Map<string, { list: BarbarianVillageData[]; t: number }>()
const allianceAreasListCache = new Map<string, { list: AllianceAreaData[]; t: number }>()
const userCastleMemoryCache = new Map<string, { data: CastleData | null; t: number }>()
const BULK_LIST_TTL_MS = 5 * 60 * 1000
const USER_CASTLE_TTL_MS = 1.5 * 60 * 1000
const GARRISON_CACHE_TTL_MS = 2 * 60 * 1000

const garrisonCache = new Map<
  string,
  { defenders: BarbarianVillageDefender[]; transfers: BarbarianVillageTransfer[]; max: number; t: number }
>()

function garrisonCacheKey(worldId: string, villageId: string) {
  return `${worldId}:${villageId}`
}

/** Sync read — instant modal paint when garrison was prefetched or recently opened. */
export function peekBarbarianVillageGarrisonCache(
  worldId: string,
  villageId: string,
): { defenders: BarbarianVillageDefender[]; transfers: BarbarianVillageTransfer[]; max: number } | null {
  const hit = garrisonCache.get(garrisonCacheKey(worldId, villageId))
  if (!hit || Date.now() - hit.t > GARRISON_CACHE_TTL_MS) return null
  return { defenders: hit.defenders, transfers: hit.transfers, max: hit.max }
}

export function invalidateBarbarianVillageGarrisonCache(worldId: string, villageId?: string) {
  if (villageId) {
    garrisonCache.delete(garrisonCacheKey(worldId, villageId))
    return
  }
  for (const k of garrisonCache.keys()) {
    if (k.startsWith(`${worldId}:`)) garrisonCache.delete(k)
  }
}

/** Call after mutations so lists / chunks / home castle refresh. */
export function invalidateWorldMapDataCaches(worldId: string) {
  castlesListCache.delete(worldId)
  barbarianListCache.delete(worldId)
  allianceAreasListCache.delete(worldId)
  activeAttacksCache.delete(worldId)
  userCastleMemoryCache.delete(worldId)
  for (const k of chunkDataMemoryCache.keys()) {
    if (k.startsWith(`${worldId}-`)) {
      chunkDataMemoryCache.delete(k)
      chunkDataMemoryTs.delete(k)
    }
  }
}

/**
 * Drops cached `/world-map/alliance-areas` so the next `getAllianceAreas` bypasses TTL.
 * Call after alliance roster changes (create / leave / join / kick / dismantle). Omit `worldId` to clear all worlds.
 */
export function invalidateAllianceAreasListCache(worldId?: string) {
  const w = worldId != null ? String(worldId).trim() : ''
  if (w) allianceAreasListCache.delete(w)
  else allianceAreasListCache.clear()
}

const worldPackMetaCache = new Map<string, Promise<{ seed?: number; width?: number; height?: number } | null>>()

async function fetchWorldPackMeta(
  baseUrl: string,
): Promise<{ seed?: number; width?: number; height?: number } | null> {
  const key = baseUrl.replace(/\/+$/, '')
  let p = worldPackMetaCache.get(key)
  if (!p) {
    p = (async () => {
      try {
        const res = await fetch(`${key}/world-meta.json`)
        if (!res.ok) return null
        const m = (await res.json()) as Record<string, unknown>
        const out: { seed?: number; width?: number; height?: number } = {}
        if (typeof m.seed === 'number' && Number.isFinite(m.seed)) out.seed = m.seed
        if (typeof m.width === 'number' && Number.isFinite(m.width) && m.width > 0) out.width = m.width
        if (typeof m.height === 'number' && Number.isFinite(m.height) && m.height > 0) out.height = m.height
        return Object.keys(out).length ? out : null
      } catch {
        return null
      }
    })()
    worldPackMetaCache.set(key, p)
  }
  return p
}

/** Reused for the SPA session so leaving and re-entering the map skips another `/config` round-trip. */
let cachedWorldConfig: WorldConfig | null = null

/** Synchronous read after {@link warmWorldMapConfig} / first `getWorldConfig` resolves — avoids awaits on revisit. */
export function peekCachedWorldConfig(): WorldConfig | null {
  return cachedWorldConfig
}

/**
 * Loads world-map config. Merges optional env-driven pack URL and (when using that URL)
 * `world-meta.json` so procedural terrain matches the generator without editing Mongo.
 */
export async function getWorldConfig(): Promise<WorldConfig> {
  if (cachedWorldConfig) return cachedWorldConfig

  const { data } = await axios.get(`${BASE}/config`)
  let cfg = data.data as WorldConfig

  const envPack = (process.env.REACT_APP_GENERATED_MAP_PACK_URL || '').trim()
  if (envPack && (cfg.generatedMapPackUrl == null || String(cfg.generatedMapPackUrl).trim() === '')) {
    cfg = { ...cfg, generatedMapPackUrl: envPack }
  }

  let packBase = (cfg.generatedMapPackUrl || '').trim()
  /** When API omits a pack URL but the SPA ships `public/assets/maps/world-pack`, region checks still work. */
  if (!packBase && process.env.REACT_APP_DISABLE_DEFAULT_WORLD_PACK !== 'true') {
    packBase = '/assets/maps/world-pack'
    cfg = { ...cfg, generatedMapPackUrl: packBase }
  }

  const syncFromPack =
    !!envPack || process.env.REACT_APP_SYNC_WORLD_FROM_PACK === 'true'

  if (packBase && syncFromPack) {
    const meta = await fetchWorldPackMeta(packBase)
    if (meta) {
      cfg = {
        ...cfg,
        ...(meta.seed !== undefined ? { seed: meta.seed } : {}),
        ...(meta.width !== undefined ? { width: meta.width } : {}),
        ...(meta.height !== undefined ? { height: meta.height } : {}),
      }
    }
  }

  cachedWorldConfig = cfg
  return cfg
}

export async function getChunkDynamicData(
  worldId: string,
  cx: number,
  cy: number,
): Promise<ChunkDynamicData> {
  const cacheKey = `${worldId}-${cx}-${cy}`

  const memTs = chunkDataMemoryTs.get(cacheKey)
  if (memTs != null && Date.now() - memTs < CHUNK_DATA_MEMORY_TTL_MS) {
    const mem = chunkDataMemoryCache.get(cacheKey)
    if (mem) return mem
  }

  // Return cached promise if request is already in flight
  if (chunkRequestCache.has(cacheKey)) {
    return chunkRequestCache.get(cacheKey)!
  }

  const promise = (async (): Promise<ChunkDynamicData> => {
    await acquireChunkFetchSlot()
    try {
      try {
        const { data } = await axios.get(`${BASE}/chunk`, {
          params: { worldId, cx, cy },
          timeout: 10000, // 10 second timeout to prevent hanging requests
        })
        if (data.success && data.data) {
          const out = data.data as ChunkDynamicData
          chunkDataMemoryCache.set(cacheKey, out)
          chunkDataMemoryTs.set(cacheKey, Date.now())
          return out
        }
        const empty: ChunkDynamicData = { castles: [] }
        chunkDataMemoryCache.set(cacheKey, empty)
        chunkDataMemoryTs.set(cacheKey, Date.now())
        return empty

      } catch (error: any) {
        // Handle cancellation errors silently
        if (error?.name === 'CanceledError' || error?.message === 'canceled' || error?.isCanceled) {
          return { castles: [] }
        }

        // Handle timeout errors
        if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
          console.warn(`getChunkDynamicData: Request timeout for chunk ${cx},${cy}`)
          return { castles: [] }
        }

        // Handle rate limiting
        if (error.response?.status === 429) {
          console.warn(`getChunkDynamicData: Rate limited for chunk ${cx},${cy}`)
          return { castles: [] }
        }

        // Handle other errors
        if (error.response?.status >= 500) {
          console.warn(`getChunkDynamicData: Server error for chunk ${cx},${cy}:`, error.response?.status)
        } else if (error.response?.status !== 404) {
          console.warn(`getChunkDynamicData: Error for chunk ${cx},${cy}:`, error.message)
        }

        return { castles: [] }
      }
    } finally {
      releaseChunkFetchSlot()
      setTimeout(() => chunkRequestCache.delete(cacheKey), 5000)
    }
  })()

  chunkRequestCache.set(cacheKey, promise)
  return promise
}

export async function placeCastle(
  worldId: string,
  q: number,
  r: number,
): Promise<CastleData> {
  const { data } = await axios.post(`${BASE}/castle`, { worldId, q, r })
  invalidateWorldMapDataCaches(worldId)
  return data.data
}

export interface MoveCastleResult {
  castle: CastleData
  ecsTokenAmount?: number
}

export async function moveCastle(
  worldId: string,
  q: number,
  r: number,
): Promise<MoveCastleResult> {
  const res = await axios.post(`${BASE}/castle/move`, { worldId, q, r })
  const body = res.data as {
    success?: boolean
    data?: CastleData
    ecsTokenAmount?: number
    message?: string
  }
  if (body && body.success === false) {
    const err = new Error(body.message || 'Castle move failed') as Error & {
      response?: typeof res
    }
    err.response = res
    throw err
  }
  const castle = body?.data
  if (
    !castle ||
    castle.q === undefined ||
    castle.r === undefined ||
    castle._id === undefined
  ) {
    const err = new Error(
      'Invalid server response for castle move — try again or refresh the page.',
    ) as Error & { response?: typeof res }
    err.response = res
    throw err
  }
  invalidateWorldMapDataCaches(worldId)
  const ecsRaw = body.ecsTokenAmount
  const ecsTokenAmount =
    typeof ecsRaw === 'number' && Number.isFinite(ecsRaw) ? ecsRaw : undefined
  return ecsTokenAmount !== undefined ? { castle, ecsTokenAmount } : { castle }
}

export async function getAllCastles(
  worldId: string,
  opts?: {
    /**
     * Restrict to the viewer's own strategic region (server-side filter).
     * Used by the espionage target map — spy missions are same-region only,
     * while the regular world map shows every region.
     */
    regionScope?: boolean
  },
): Promise<CastleData[]> {
  /**
   * No client-side TTL: always hit the network so we don't serve a stale list
   * when something changed via socket while the world-map page was unmounted
   * (e.g. another alliance member joined and you came back to /world-map after
   * a brief visit elsewhere). The backend Redis cache (`getCachedAllCastles`)
   * already absorbs cost — typical warm response is sub-200 ms.
   */
  try {
    const { data } = await axios.get(`${BASE}/castles`, {
      params: opts?.regionScope ? { worldId, scope: 'region' } : { worldId },
    })
    if (data.success && Array.isArray(data.data)) {
      const list = data.data as CastleData[]
      // Only the full (world-wide) list feeds the shared fallback cache.
      if (!opts?.regionScope) castlesListCache.set(worldId, { list, t: Date.now() })
      return list
    }
    console.warn('getAllCastles: Invalid response format', data)
    return []
  } catch (error) {
    console.error('getAllCastles: API error', error)
    return []
  }
}

export type BarbarianVillageDefenseAlert = {
  attackId: string
  defenderWallet: string
  attackerWallet?: string
  attackerUsername?: string | null
  arrivesAt: string
  toQ: number
  toR: number
  attackType: 'barbarian_village'
  barbarianVillageId: string
  barbarianWorldId: string
  marchPhase: 'march' | 'choose' | 'resolved' | 'arrived'
  awaitingDefenderChoice?: boolean
  choiceUntil?: string | null
  rallyMode?: boolean
}

export async function getBarbarianVillageDefenseAlerts(): Promise<BarbarianVillageDefenseAlert[]> {
  try {
    const { data } = await axios.get(`${BASE}/barbarian-village/defense-alerts`)
    if (data?.success && Array.isArray(data.data)) {
      return data.data as BarbarianVillageDefenseAlert[]
    }
    return []
  } catch (e) {
    console.warn('getBarbarianVillageDefenseAlerts', e)
    return []
  }
}

export async function getAllBarbarianVillages(worldId: string): Promise<BarbarianVillageData[]> {
  /**
   * No client-side TTL — same reason as `getAllCastles`. Backend Redis cache
   * (`getCachedAllBarbarians`) handles the dedup cost.
   */
  try {
    const { data } = await axios.get(`${BASE}/barbarian-villages`, {
      params: { worldId },
    })
    if (data.success && Array.isArray(data.data)) {
      const list = data.data as BarbarianVillageData[]
      barbarianListCache.set(worldId, { list, t: Date.now() })
      return list
    }
    console.warn('getAllBarbarianVillages: Invalid response format', data)
    return []
  } catch (error) {
    console.error('getAllBarbarianVillages: API error', error)
    return []
  }
}

export type ResolveBarbarianVillageAttackResult = {
  ok?: boolean
  barbarian?: BarbarianVillageData
  error?: string
  lootWon?: { wood?: number; stone?: number; iron?: number }
}

export async function resolveBarbarianVillageAttack(
  worldId: string,
  barbarianVillageId: string,
  attackerPower: number,
  capture: boolean,
  opts?: { autoPveTier?: boolean },
): Promise<ResolveBarbarianVillageAttackResult> {
  const { data } = await axios.post(`${BASE}/barbarian-village/resolve`, {
    worldId,
    barbarianVillageId,
    attackerPower,
    capture,
    autoPveTier: opts?.autoPveTier === true,
  })
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'resolve_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  invalidateWorldMapDataCaches(worldId)
  return data.data as ResolveBarbarianVillageAttackResult
}

export type BarbarianVillagePvpResult = {
  ok?: boolean
  /** 'hex' → owner online: a hex battle was created (navigate to HexBattlePage with battleId). 'auto' → resolved by power. */
  mode?: 'hex' | 'auto'
  battleId?: string
  /** auto mode only */
  battleOutcome?: 'attacker_wins' | 'defender_holds'
  lootWon?: { wood?: number; stone?: number; iron?: number }
  playerPower?: { totalAttack: number; totalHp: number; totalArmor: number; count: number }
  characters?: AutoBattleCharacterInfo[]
  characterDamages?: CharacterDamageInfo[]
  pveXp?: { userXp?: number; characterXpEach?: number }
}

/**
 * Attack an OWNED barbarian village (PvP). On owner-online it returns `{ mode:'hex', battleId }`
 * (caller navigates to the HexBattlePage); otherwise `{ mode:'auto', battleOutcome, ... }`.
 */
export async function pvpAttackBarbarianVillage(
  worldId: string,
  barbarianVillageId: string,
  characterIds: string[],
): Promise<BarbarianVillagePvpResult> {
  const { data } = await axios.post(`${BASE}/barbarian-village/pvp-attack`, {
    worldId,
    barbarianVillageId,
    characterIds,
  })
  if (!data?.success || !data?.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(data?.message || 'pvp_failed')
    err.response = { data: { message: data?.message } }
    throw err
  }
  invalidateWorldMapDataCaches(worldId)
  return data.data as BarbarianVillagePvpResult
}

/**
 * Owner clicked "Auto Battle" on the owned-village defence choice modal — hand the defence of the
 * already-created hex battle to the AI (the attacker keeps playing their side).
 */
export async function barbarianVillageHexAutoDefend(battleId: string): Promise<void> {
  const { data } = await axios.post(`${BASE}/barbarian-village/hex-auto-defend`, { battleId })
  if (!data?.success) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(data?.message || 'auto_defend_failed')
    err.response = { data: { message: data?.message } }
    throw err
  }
}

/**
 * Owner picked "Enter Battle" on the owned-village defence prompt → server builds the hex battle now (vs
 * the village's stationed defenders) and returns the `battleId` to navigate into. The attacker is signalled
 * to navigate via the `world:pve-march:hex-battle-started` socket.
 */
export async function enterBarbarianVillageDefense(
  worldId: string,
  barbarianVillageId: string,
): Promise<{ battleId: string }> {
  const { data } = await axios.post(`${BASE}/barbarian-village/defense/enter`, { worldId, barbarianVillageId })
  if (!data?.success || !data?.data?.battleId) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(data?.message || 'enter_defense_failed')
    err.response = { data: { message: data?.message } }
    throw err
  }
  return { battleId: String(data.data.battleId) }
}

/**
 * Owner picked "Auto Battle" → pure auto power-resolve vs the stationed defenders (no hex, no redirect).
 * The owner's result + the attacker's result both arrive over sockets.
 */
export async function autoBarbarianVillageDefense(
  worldId: string,
  barbarianVillageId: string,
): Promise<AutoBattleResult> {
  const { data } = await axios.post(`${BASE}/barbarian-village/defense/auto`, { worldId, barbarianVillageId })
  if (!data?.success || !data?.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(data?.message || 'auto_defense_failed')
    err.response = { data: { message: data?.message } }
    throw err
  }
  return data.data as AutoBattleResult
}

/** List the owner's stationed defenders at an owned village (owner-only). */
export async function getBarbarianVillageGarrison(
  worldId: string,
  barbarianVillageId: string,
  opts?: { force?: boolean },
): Promise<{ defenders: BarbarianVillageDefender[]; transfers: BarbarianVillageTransfer[]; max: number }> {
  const key = garrisonCacheKey(worldId, barbarianVillageId)
  if (!opts?.force) {
    const cached = peekBarbarianVillageGarrisonCache(worldId, barbarianVillageId)
    if (cached) return cached
  }
  const { data } = await axios.get(`${BASE}/barbarian-village/garrison`, {
    params: { worldId, barbarianVillageId },
  })
  const result = {
    defenders: Array.isArray(data?.defenders) ? data.defenders : [],
    transfers: Array.isArray(data?.transfers) ? data.transfers : [],
    max: Number(data?.max) || 10,
  }
  garrisonCache.set(key, { ...result, t: Date.now() })
  return result
}

/** Station available characters as village defenders (owner-only). Throws on failure (message in err.response.data.message). */
export async function stationBarbarianVillageDefenders(
  worldId: string,
  barbarianVillageId: string,
  characterIds: string[],
): Promise<{ stationed: number; marching: number; travelTimeMs: number; arrivalAt: string | null; barbarian?: BarbarianVillageData }> {
  const { data } = await axios.post(`${BASE}/barbarian-village/garrison/station`, {
    worldId,
    barbarianVillageId,
    characterIds,
  })
  invalidateWorldMapDataCaches(worldId)
  invalidateBarbarianVillageGarrisonCache(worldId, barbarianVillageId)
  return {
    stationed: Number(data?.stationed) || 0,
    marching: Number(data?.marching) || 0,
    travelTimeMs: Number(data?.travelTimeMs) || 0,
    arrivalAt: data?.arrivalAt ?? null,
    barbarian: data?.barbarian,
  }
}

/** Withdraw stationed defenders back to the owner's pool (multi-select; owner-only). Champions march home (timed). */
export async function withdrawBarbarianVillageDefenders(
  worldId: string,
  barbarianVillageId: string,
  characterIds: string[],
): Promise<{ withdrawn: number; marching: number; travelTimeMs: number; arrivalAt: string | null; barbarian?: BarbarianVillageData }> {
  const { data } = await axios.post(`${BASE}/barbarian-village/garrison/withdraw`, {
    worldId,
    barbarianVillageId,
    characterIds,
  })
  invalidateWorldMapDataCaches(worldId)
  invalidateBarbarianVillageGarrisonCache(worldId, barbarianVillageId)
  return {
    withdrawn: Number(data?.withdrawn) || 0,
    marching: Number(data?.marching) || 0,
    travelTimeMs: Number(data?.travelTimeMs) || 0,
    arrivalAt: data?.arrivalAt ?? null,
    barbarian: data?.barbarian,
  }
}

/**
 * Give an owned village back to the barbarians (owner-only).
 *
 * Unlike a withdraw, the champions are not sent on a timed march — the village stops being yours
 * immediately, so every stationed and in-transit champion is released to the available pool on the
 * spot. Refused by the server while a fight is live on the village.
 */
export async function abandonBarbarianVillage(
  worldId: string,
  barbarianVillageId: string,
): Promise<{ releasedCharacterCount: number; village?: BarbarianVillageData }> {
  const { data } = await axios.post(`${BASE}/barbarian-village/abandon`, {
    worldId,
    barbarianVillageId,
  })
  invalidateWorldMapDataCaches(worldId)
  invalidateBarbarianVillageGarrisonCache(worldId, barbarianVillageId)
  return {
    releasedCharacterCount: Number(data?.releasedCharacterCount) || 0,
    village: data?.village,
  }
}

export async function getUserCastle(worldId: string): Promise<CastleData | null> {
  const hit = userCastleMemoryCache.get(worldId)
  if (hit && Date.now() - hit.t < USER_CASTLE_TTL_MS) return hit.data

  try {
    const { data } = await axios.get(`${BASE}/user-castle`, {
      params: { worldId },
    })
    const justCreated = data?.castleJustProvisioned === true
    if (justCreated) {
      invalidateWorldMapDataCaches(worldId)
    }
    let out: CastleData | null = null
    if (data.success && data.data) {
      out = data.data as CastleData
    } else if (data?.locked === true) {
      // World map not unlocked yet (below the castle-unlock account level) — expected empty state.
    } else {
      console.warn('getUserCastle: Invalid response format', data)
    }
    userCastleMemoryCache.set(worldId, { data: out, t: Date.now() })
    return out
  } catch (error: any) {
    if (error.response?.status === 429) {
      console.warn('getUserCastle: Rate limited')
    } else {
      console.error('getUserCastle: API error', error)
    }
    return null
  }
}

/** A player's castle coordinate, resolved via {@link getCastleByWallet} — location-only, no ownerId/flag/alliance fields. */
export interface WalletCastleLocation {
  q: number
  r: number
  level: number
}

const walletCastleMemoryCache = new Map<string, { data: WalletCastleLocation | null; t: number }>()
const WALLET_CASTLE_TTL_MS = 1.5 * 60 * 1000

/**
 * Read-only coordinate lookup for an ARBITRARY player (e.g. showing a friend's MvM
 * location in the Friends tab) — unlike {@link getUserCastle}, which only ever resolves
 * the caller's own castle. Returns null when that wallet hasn't placed a castle yet.
 */
export async function getCastleByWallet(worldId: string, walletAddress: string): Promise<WalletCastleLocation | null> {
  const key = `${worldId}:${walletAddress.toLowerCase()}`
  const hit = walletCastleMemoryCache.get(key)
  if (hit && Date.now() - hit.t < WALLET_CASTLE_TTL_MS) return hit.data

  try {
    // Param is `targetWalletAddress`, not `walletAddress` — see the backend handler's
    // note: `authApiKey`'s ownership guard 403s any `walletAddress` field that doesn't
    // match the caller's own wallet, and this lookup is deliberately for someone else's.
    const { data } = await axios.get(`${BASE}/castle-by-wallet`, {
      params: { worldId, targetWalletAddress: walletAddress },
    })
    const out: WalletCastleLocation | null = data?.success && data?.data ? (data.data as WalletCastleLocation) : null
    walletCastleMemoryCache.set(key, { data: out, t: Date.now() })
    return out
  } catch (error: any) {
    if (error.response?.status !== 429) {
      console.error('getCastleByWallet: API error', error)
    }
    return null
  }
}

export async function getAllianceAreas(worldId: string): Promise<AllianceAreaData[]> {
  /**
   * No client-side TTL: when *any* alliance member (yours or another alliance's)
   * joins / leaves / is kicked, the backend recalcs and emits
   * `world:alliance-area:updated`. But if your tab was on another page when that
   * socket event fired, the in-memory listener was gone, and on returning to
   * /world-map a cached entry would serve the pre-change polygon for up to 5 min.
   * Always hitting the network keeps polygons truthful; backend Redis absorbs cost.
   */
  const { data } = await axios.get(`${BASE}/alliance-areas`, {
    params: { worldId },
  })
  /**
   * Throw on bad response so callers can `.catch()` and skip the engine
   * update — returning `[]` here used to silently wipe every visible polygon
   * via `setAllianceAreas([])`.
   */
  if (!data?.success || !Array.isArray(data?.data)) {
    throw new Error('Invalid /alliance-areas response: ' + JSON.stringify(data)?.slice(0, 200))
  }
  const list = data.data as AllianceAreaData[]
  allianceAreasListCache.set(worldId, { list, t: Date.now() })
  return list
}

export async function claimAllianceArea(worldId: string): Promise<AllianceAreaData | null> {
  try {
    const { data } = await axios.post(`${BASE}/alliance-area/claim`, { worldId })
    if (data.success && data.data) {
      invalidateWorldMapDataCaches(worldId)
      return data.data
    }
    return null
  } catch (error) {
    console.error('claimAllianceArea: API error', error)
    return null
  }
}

export async function extendAllianceArea(
  worldId: string,
  tiles: HexCoord[],
): Promise<AllianceAreaData | null> {
  try {
    const { data } = await axios.post(`${BASE}/alliance-area/extend`, { worldId, tiles })
    if (data.success && data.data) {
      invalidateWorldMapDataCaches(worldId)
      return data.data
    }
    return null
  } catch (error) {
    console.error('extendAllianceArea: API error', error)
    return null
  }
}

export async function autoClaimAllianceArea(worldId: string): Promise<AllianceAreaData | null> {
  try {
    const { data } = await axios.post(`${BASE}/alliance-area/auto-claim`, { worldId })
    if (data.success && data.data) {
      invalidateWorldMapDataCaches(worldId)
      return data.data
    }
    return null
  } catch (error) {
    console.error('autoClaimAllianceArea: API error', error)
    return null
  }
}

const activeAttacksCache = new Map<string, { list: WorldAttackData[]; t: number }>()
const ACTIVE_ATTACKS_TTL_MS = 15 * 1000 // 15 s — frequent enough to see incoming raids, not so frequent it hammers the server on every map mount

/** Invalidated by {@link invalidateWorldMapDataCaches}. */
export function invalidateActiveAttacksCache(worldId: string) {
  activeAttacksCache.delete(worldId)
}

export async function getActiveAttacks(worldId: string): Promise<WorldAttackData[]> {
  const hit = activeAttacksCache.get(worldId)
  if (hit && Date.now() - hit.t < ACTIVE_ATTACKS_TTL_MS) return hit.list

  try {
    const { data } = await axios.get(`${BASE}/active-attacks`, { params: { worldId } })
    if (data.success && Array.isArray(data.data)) {
      const list = data.data as WorldAttackData[]
      activeAttacksCache.set(worldId, { list, t: Date.now() })
      return list
    }
    return []
  } catch (error) {
    console.error('getActiveAttacks: API error', error)
    return []
  }
}
/**
 * Authoritative live-siege snapshot, always from the server, or `null` when the request failed. Callers that
 * prune map lines against this list must skip the prune on `null` — an empty list means "no sieges", a
 * failed request means nothing.
 */
export async function fetchActiveAttacksFresh(worldId: string): Promise<WorldAttackData[] | null> {
  try {
    const { data } = await axios.get(`${BASE}/active-attacks`, { params: { worldId } })
    if (data.success && Array.isArray(data.data)) {
      const list = data.data as WorldAttackData[]
      activeAttacksCache.set(worldId, { list, t: Date.now() })
      return list
    }
    return null
  } catch (error) {
    console.error('fetchActiveAttacksFresh: API error', error)
    return null
  }
}

/** Regional boundary tower (defensive gate) — pack geometry + server ownership. */
export interface BoundaryTowerData {
  crossingKey: string
  regionA: string
  regionB: string
  q: number
  r: number
  bridgeHexes: { q: number; r: number }[]
  packExitIds: string[]
  blockedByPack: boolean
  monsterLevel?: number
  garrisonPower?: number
  ownerId: string | null
  /** Player display name from world castle (`ownerName`); not a wallet string. */
  ownerDisplayName?: string | null
  /** Alliance name when `ownerAllianceId` is set. */
  ownerAllianceName?: string | null
  capturedAt: string | null
  underAttackBy?: string | null
  underAttackUntil?: string | null
  /** Alliance that owns the capturing player — used with alliance diplomacy for cross-region route UI. */
  ownerAllianceId?: string | null
  /** Server march duration — pairs with `underAttackUntil` for reload-safe PvE arrow timing. */
  pveMarchTravelTimeMs?: number | null
  pveMarchWaypointHexes?: Array<{ q: number; r: number }>
}

const boundaryTowersCache = new Map<string, { list: BoundaryTowerData[]; t: number }>()
const boundaryTowersRequestCache = new Map<string, Promise<BoundaryTowerData[]>>()
const BOUNDARY_TOWERS_TTL_MS = 15 * 1000

export function invalidateBoundaryTowersCache(worldId?: string) {
  const w = worldId != null ? String(worldId).trim() : ''
  if (w) {
    boundaryTowersCache.delete(w)
    boundaryTowersRequestCache.delete(w)
  } else {
    boundaryTowersCache.clear()
    boundaryTowersRequestCache.clear()
  }
}

export async function getBoundaryTowers(worldId: string): Promise<BoundaryTowerData[]> {
  const hit = boundaryTowersCache.get(worldId)
  if (hit && Date.now() - hit.t < BOUNDARY_TOWERS_TTL_MS) return hit.list

  const inFlight = boundaryTowersRequestCache.get(worldId)
  if (inFlight) return inFlight

  const promise = (async (): Promise<BoundaryTowerData[]> => {
    try {
      const { data } = await axios.get(`${BASE}/boundary-towers`, { params: { worldId } })
      if (data.success && data.data?.towers && Array.isArray(data.data.towers)) {
        const list = data.data.towers as BoundaryTowerData[]
        boundaryTowersCache.set(worldId, { list, t: Date.now() })
        return list
      }
      const empty: BoundaryTowerData[] = []
      boundaryTowersCache.set(worldId, { list: empty, t: Date.now() })
      return empty
    } catch (error) {
      console.error('getBoundaryTowers: API error', error)
      return []
    } finally {
      boundaryTowersRequestCache.delete(worldId)
    }
  })()

  boundaryTowersRequestCache.set(worldId, promise)
  return promise
}

export async function postCaptureBoundaryTower(
  worldId: string,
  crossingKey: string,
): Promise<BoundaryTowerData | null> {
  const { data } = await axios.post(`${BASE}/boundary-tower/capture`, { worldId, crossingKey })
  if (data.success && data.data) {
    invalidateBoundaryTowersCache(worldId)
    return data.data as BoundaryTowerData
  }
  return null
}

export type ResolveDefenseTowerAttackResult = {
  ok?: boolean
  tower?: BoundaryTowerData
  error?: string
}

export async function resolveDefenseTowerAttack(
  worldId: string,
  crossingKey: string,
  attackerPower: number,
): Promise<ResolveDefenseTowerAttackResult> {
  const { data } = await axios.post(`${BASE}/boundary-tower/attack-resolve`, {
    worldId,
    crossingKey,
    attackerPower,
  })
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'resolve_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  invalidateBoundaryTowersCache(worldId)
  return data.data as ResolveDefenseTowerAttackResult
}

export async function postDefenseTowerAfterCombat(
  worldId: string,
  crossingKey: string,
  outcome: 'attacker_wins' | 'defender_holds',
  attackerPower?: number,
): Promise<ResolveDefenseTowerAttackResult> {
  const { data } = await axios.post(`${BASE}/boundary-tower/after-combat`, {
    worldId,
    crossingKey,
    outcome,
    attackerPower: attackerPower ?? 0,
  })
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'update_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  invalidateBoundaryTowersCache(worldId)
  return data.data as ResolveDefenseTowerAttackResult
}

export type AutoBattleCharacterInfo = {
  characterId: string
  characterName: string
  rarity: string
  level: number
  attackPower: number
  maxHp: number
  armor: number
}

export type CharacterDamageInfo = {
  characterId: string
  characterName: string
  hpBefore: number
  hpAfter: number
  hpLost: number
  maxHp: number
}

export type PveXpReward = {
  userXp: number
  characterXpEach: number
  characterCount: number
  totalCharacterXp: number
  userLeveledUp?: boolean
  userNewLevel?: number
}

export type AutoBattleResult = {
  ok?: boolean
  /** Server already resolved (e.g. scheduler) — client should not show result modal or return march. */
  skipped?: boolean
  reason?: string
  autoBattle?: boolean
  battleOutcome?: 'attacker_wins' | 'defender_holds' | string
  playerPower?: { totalAttack: number; totalHp: number; totalArmor: number; count: number }
  garrisonPower?: number
  characters?: AutoBattleCharacterInfo[]
  characterDamages?: CharacterDamageInfo[]
  lootWon?: { wood?: number; stone?: number; iron?: number }
  /** When present (e.g. barbarian / tower auto-battle), shown on MvM-style result modals. */
  characterXp?: number
  userXp?: number
  barbarian?: BarbarianVillageData
  tower?: BoundaryTowerData
  error?: string
  /** Server-applied XP from world-map PvE (barbarian / neutral tower). */
  pveXp?: PveXpReward | null
  /**
   * Owned-village PvP outcome routing:
   *  - 'hex' → a hex battle was created (navigate to HexBattlePage with `battleId`);
   *  - 'auto' → power-resolved;
   *  - 'awaiting_defender' → the owner is online with defenders: the attacker WAITS while the owner picks
   *    Enter Battle (→ hex via `world:pve-march:hex-battle-started`) or Auto Battle (→ result via
   *    `world:pve-march:auto-resolved`). No redirect, no return march until that socket arrives.
   */
  mode?: 'hex' | 'auto' | 'awaiting_defender'
  battleId?: string
  /** When `mode==='awaiting_defender'`: ISO deadline after which the server auto-resolves the defence. */
  choiceUntil?: string
  /**
   * Server-authoritative home leg for a wild-village raid, which creates no PendingAttack and so returns
   * nothing from `/mvm/pending-attacks`. Present on a resolved auto-battle; drives the return arrow with
   * the server's own timing instead of a locally recomputed guess.
   */
  returnMarch?: {
    fromQ: number
    fromR: number
    toQ: number
    toR: number
    marchMs: number
    returnArrivesAt: string
  }
}

/** True when a world-map PvE auto-battle outcome counts as an attacker victory (incl. wild conquer). */
export function isPveAutoBattleAttackerWin(outcome: string | undefined | null): boolean {
  const o = String(outcome || '')
  return o === 'attacker_wins' || o.startsWith('attacker_wins')
}

export async function autoBattleBarbarianVillage(
  worldId: string,
  barbarianVillageId: string,
  characterIds?: string[],
): Promise<AutoBattleResult> {
  const { data } = await axios.post(`${BASE}/barbarian-village/auto-battle`, {
    worldId,
    barbarianVillageId,
    ...(characterIds?.length ? { characterIds } : {}),
  })
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'auto_battle_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  const payload = data.data as AutoBattleResult
  if (payload.skipped) {
    // Preserve `mode`/`reason` so the caller can tell an "awaiting the owner's choice" skip (army stays
    // parked) apart from an "already resolved" skip (army marches home).
    return { skipped: true, reason: payload.reason, mode: payload.mode }
  }
  invalidateWorldMapDataCaches(worldId)
  return payload
}

export async function autoBattleDefenseTower(
  worldId: string,
  crossingKey: string,
  characterIds?: string[],
): Promise<AutoBattleResult> {
  const { data } = await axios.post(`${BASE}/boundary-tower/auto-battle`, {
    worldId,
    crossingKey,
    ...(characterIds?.length ? { characterIds } : {}),
  })
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'auto_battle_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  const payload = data.data as AutoBattleResult
  if (payload.skipped) {
    return { skipped: true, reason: payload.reason }
  }
  invalidateBoundaryTowersCache(worldId)
  return payload
}

// ======================== Attack Locks ========================

export type LockPveMarchOk = {
  ok: true
  travelTimeMs: number
  marchWaypointHexes?: Array<{ q: number; r: number }>
  energy?: number
  energyUpdatedAt?: string
}

export async function lockBarbarianForAttack(
  worldId: string,
  barbarianVillageId: string,
  travelTimeMs: number,
  characterIds: string[],
): Promise<LockPveMarchOk | { ok: false; error?: string }> {
  try {
    const { data } = await axios.post(`${BASE}/barbarian-village/lock-for-attack`, {
      worldId,
      barbarianVillageId,
      travelTimeMs,
      characterIds,
    })
    if (!data.success) {
      return { ok: false, error: data.message || 'lock_failed' }
    }
    invalidateWorldMapDataCaches(worldId)
    invalidateBoundaryTowersCache(worldId)
    const d = data.data as {
      travelTimeMs?: number
      marchWaypointHexes?: Array<{ q: number; r: number }>
      energy?: number
      energyUpdatedAt?: string
    }
    return {
      ok: true,
      travelTimeMs: Math.max(0, Math.floor(Number(d?.travelTimeMs) || 0)),
      marchWaypointHexes: d?.marchWaypointHexes,
      energy: d?.energy,
      energyUpdatedAt: d?.energyUpdatedAt,
    }
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { message?: string } }; message?: string }
    return { ok: false, error: ax?.response?.data?.message || ax?.message || 'lock_failed' }
  }
}

export async function lockTowerForAttack(
  worldId: string,
  crossingKey: string,
  travelTimeMs: number,
  characterIds: string[],
): Promise<LockPveMarchOk | { ok: false; error?: string }> {
  try {
    const { data } = await axios.post(`${BASE}/boundary-tower/lock-for-attack`, {
      worldId,
      crossingKey,
      travelTimeMs,
      characterIds,
    })
    if (!data.success) {
      return { ok: false, error: data.message || 'lock_failed' }
    }
    const d = data.data as {
      travelTimeMs?: number
      marchWaypointHexes?: Array<{ q: number; r: number }>
    }
    return {
      ok: true,
      travelTimeMs: Math.max(0, Math.floor(Number(d?.travelTimeMs) || 0)),
      marchWaypointHexes: d?.marchWaypointHexes,
    }
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { message?: string } }; message?: string }
    return { ok: false, error: ax?.response?.data?.message || ax?.message || 'lock_failed' }
  }
}

export async function cancelBarbarianMarchLock(
  worldId: string,
  barbarianVillageId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data } = await axios.post(`${BASE}/barbarian-village/cancel-march-lock`, {
      worldId,
      barbarianVillageId,
    })
    if (!data.success) {
      return { ok: false, error: data.message || 'cancel_failed' }
    }
    invalidateWorldMapDataCaches(worldId)
    return { ok: true }
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { message?: string } }; message?: string }
    return { ok: false, error: ax?.response?.data?.message || ax?.message || 'cancel_failed' }
  }
}

export async function cancelTowerMarchLock(
  worldId: string,
  crossingKey: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data } = await axios.post(`${BASE}/boundary-tower/cancel-march-lock`, {
      worldId,
      crossingKey,
    })
    if (!data.success) {
      return { ok: false, error: data.message || 'cancel_failed' }
    }
    invalidateBoundaryTowersCache(worldId)
    return { ok: true }
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { message?: string } }; message?: string }
    return { ok: false, error: ax?.response?.data?.message || ax?.message || 'cancel_failed' }
  }
}

// ======================== Tower PvP ========================

export interface TowerPvPAttackResult {
  attackId: string
  travelTimeMs: number
  arrivesAt: string
  toQ: number
  toR: number
  defenderWallet: string
  defenderName?: string
  marchWaypointHexes?: Array<{ q: number; r: number }>
}

export async function startTowerPvPAttack(params: {
  worldId: string
  crossingKey: string
  fromQ: number
  fromR: number
  selectedCharacterIds: string[]
  characterTemplateIds?: string[]
  abilityPoints?: Record<string, number>
  breadAmount: number
}): Promise<TowerPvPAttackResult> {
  const { data } = await axios.post(`${BASE}/boundary-tower/pvp-attack`, params)
  if (!data.success || !data.data) {
    const err: Error & { response?: { data?: { message?: string } } } = new Error(
      data.message || 'pvp_attack_failed',
    )
    err.response = { data: { message: data.message } }
    throw err
  }
  invalidateBoundaryTowersCache(params.worldId)
  return data.data as TowerPvPAttackResult
}
